import { useCallback, useEffect, useMemo, useState } from 'react';
import { HardDrive, RefreshCw, Database, Users, CalendarClock } from 'lucide-react';
import { Button } from '../ui/button';

interface StorageCapacityTabProps {
  token: string;
}

interface StorageTableRow {
  tableName: string;
  approxRows: number;
  totalSizeBytes: number;
  totalSizePretty: string;
}

interface ClientTableBreakdown {
  tableName: string;
  rows: number;
  estimatedBytes: number;
  estimatedSizePretty: string;
}

interface ClientStorageRow {
  clientId: string;
  clientName: string;
  isActive: boolean;
  totalRows: number;
  estimatedBytes: number;
  estimatedSizePretty: string;
  historicalPoints: number;
  oldestTimestamp: string | null;
  newestTimestamp: string | null;
  historicalYearsCovered: number;
  byTable: ClientTableBreakdown[];
}

interface StorageStatsResponse {
  success: boolean;
  generatedAt: string;
  database: {
    name: string;
    type: string;
    sizeBytes: number;
    sizePretty: string;
  };
  retention: {
    logRetentionDays: number;
    systemLogRetentionDays?: number;
    historicalDataRetention: string;
    note: string;
  };
  totals: {
    trackedClientRows: number;
    trackedEstimatedBytes: number;
    trackedEstimatedSizePretty: string;
  };
  topTables: StorageTableRow[];
  perClient: ClientStorageRow[];
}

const formatDate = (value: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export function StorageCapacityTab({ token }: StorageCapacityTabProps) {
  const [stats, setStats] = useState<StorageStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch('/api/admin/storage-stats', { headers });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load storage analytics');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <Database className="h-5 w-5 text-blue-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Storage Analytics</h1>
            <p className="text-sm text-muted-foreground">Per-client storage visibility for long-term retention planning</p>
          </div>
        </div>

        <Button variant="outline" size="sm" className="gap-2" onClick={fetchStats}>
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-muted-foreground text-xs uppercase tracking-wide">Database Size</div>
          <div className="mt-2 text-2xl font-semibold text-foreground flex items-center gap-2">
            <HardDrive className="h-5 w-5 text-blue-500" /> {stats?.database?.sizePretty || '-'}
          </div>
          <div className="text-xs text-muted-foreground mt-1">{stats?.database?.name || '-'} ({stats?.database?.type || '-'})</div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-muted-foreground text-xs uppercase tracking-wide">Tracked Client Storage</div>
          <div className="mt-2 text-2xl font-semibold text-foreground flex items-center gap-2">
            <Users className="h-5 w-5 text-emerald-500" /> {stats?.totals?.trackedEstimatedSizePretty || '-'}
          </div>
          <div className="text-xs text-muted-foreground mt-1">Estimated from client-tagged table rows</div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-muted-foreground text-xs uppercase tracking-wide">Retention Policy</div>
          <div className="mt-2 text-2xl font-semibold text-foreground flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-amber-500" /> {stats?.retention?.systemLogRetentionDays ?? '-'} days (system logs)
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            General logs: {stats?.retention?.logRetentionDays ?? '-'} days | Historical data retention: {stats?.retention?.historicalDataRetention || '-'}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <h2 className="text-lg font-semibold text-foreground mb-1">Per-Client Storage</h2>
        <p className="text-sm text-muted-foreground mb-4">Estimated bytes are based on each client's row share in each table. Historical data is currently not auto-deleted.</p>

        <div className="overflow-auto max-h-[55vh] border border-border/70 rounded-lg">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/70 backdrop-blur text-left">
              <tr>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2">Estimated Size</th>
                <th className="px-3 py-2">Rows</th>
                <th className="px-3 py-2">Historical Points</th>
                <th className="px-3 py-2">Oldest Data</th>
                <th className="px-3 py-2">Newest Data</th>
                <th className="px-3 py-2">Years Covered</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-3 py-6 text-muted-foreground" colSpan={7}>Loading storage analytics...</td>
                </tr>
              ) : !stats?.perClient?.length ? (
                <tr>
                  <td className="px-3 py-6 text-muted-foreground" colSpan={7}>No client storage data available yet.</td>
                </tr>
              ) : (
                stats.perClient.map((client) => (
                  <tr key={client.clientId} className="border-t border-border/60 align-top">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="font-medium text-foreground">{client.clientName}</div>
                      <div className="text-xs text-muted-foreground">{client.clientId}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap font-medium">{client.estimatedSizePretty}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{client.totalRows.toLocaleString()}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{client.historicalPoints.toLocaleString()}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDate(client.oldestTimestamp)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDate(client.newestTimestamp)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{client.historicalYearsCovered || 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <h2 className="text-lg font-semibold text-foreground mb-3">Largest Tables</h2>
        <div className="overflow-auto border border-border/70 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-muted/70 text-left">
              <tr>
                <th className="px-3 py-2">Table</th>
                <th className="px-3 py-2">Approx Rows</th>
                <th className="px-3 py-2">Total Size</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.topTables || []).map((table) => (
                <tr key={table.tableName} className="border-t border-border/60">
                  <td className="px-3 py-2 whitespace-nowrap">{table.tableName}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{table.approxRows.toLocaleString()}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{table.totalSizePretty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
